import React, { useEffect } from "react";
import { Box, Button, Divider, Paper } from "@mui/material";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Youtube from "@tiptap/extension-youtube";

type SupportRichTextEditorProps = {
  value: string;
  onChange: (content: string) => void;
};

const SupportRichTextEditor: React.FC<SupportRichTextEditorProps> = ({
  value,
  onChange,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      Image,
      Youtube.configure({
        controls: true,
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) {
      return;
    }

    if (editor.getHTML() !== (value || "")) {
      editor.commands.setContent(value || "");
    }
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  const buttonVariant = (active: boolean) =>
    active ? "contained" : "outlined";

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", p: 1 }}>
        <Button
          variant={buttonVariant(editor.isActive("heading", { level: 1 }))}
          size="small"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
        >
          H1
        </Button>

        <Button
          variant={buttonVariant(editor.isActive("heading", { level: 2 }))}
          size="small"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        >
          H2
        </Button>

        <Button
          variant={buttonVariant(editor.isActive("paragraph"))}
          size="small"
          onClick={() => editor.chain().focus().setParagraph().run()}
        >
          Text
        </Button>

        <Divider orientation="vertical" flexItem />

        <Button
          variant={buttonVariant(editor.isActive("bold"))}
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          Bold
        </Button>

        <Button
          variant={buttonVariant(editor.isActive("italic"))}
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          Italic
        </Button>

        <Divider orientation="vertical" flexItem />

        <Button
          variant={buttonVariant(editor.isActive("bulletList"))}
          size="small"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          Bullets
        </Button>

        <Button
          variant={buttonVariant(editor.isActive("orderedList"))}
          size="small"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          Numbers
        </Button>

        <Divider orientation="vertical" flexItem />

        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            const url = window.prompt("Enter link URL");

            if (!url) {
              return;
            }

            editor.chain().focus().setLink({ href: url }).run();
          }}
        >
          Link
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={() => editor.chain().focus().unsetLink().run()}
        >
          Unlink
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            const url = window.prompt("Enter image URL");

            if (!url) {
              return;
            }

            editor.chain().focus().setImage({ src: url }).run();
          }}
        >
          Image
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            const src = window.prompt("Enter YouTube URL");

            if (!src) {
              return;
            }

            editor.commands.setYoutubeVideo({
              src,
            });
          }}
        >
          YouTube
        </Button>

        <Divider orientation="vertical" flexItem />

        <Button
          variant="outlined"
          size="small"
          onClick={() => editor.chain().focus().undo().run()}
        >
          Undo
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={() => editor.chain().focus().redo().run()}
        >
          Redo
        </Button>
      </Box>

      <Divider />

      <Box
        sx={{
          p: 2,
          minHeight: "260px",
          "& .ProseMirror": {
            minHeight: "240px",
            outline: "none",
          },
          "& .ProseMirror p": {
            marginTop: 0,
          },
          "& .ProseMirror img": {
            maxWidth: "100%",
          },
          "& .ProseMirror iframe": {
            maxWidth: "100%",
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  );
};

export default SupportRichTextEditor;
